// src/components/admin/AlarmsTab.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  Avatar,
  LinearProgress,
  Button,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SmsIcon from '@mui/icons-material/Sms';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AddAlarmIcon from '@mui/icons-material/AddAlarm';
import { useTranslation } from 'react-i18next';
import CreateAlarmDialog from '../CreateAlarmDialog';
import { useNotification } from '../../contexts/NotificationContext';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { getAlarmStatistics, getEventAlarmDetails } from '../../services/adminService';
import { AlarmStatisticsResponse, EventAlarmDetailsResponse } from '../../types/admin';

const AlarmsTab: React.FC = () => {
  const { t } = useTranslation();
  const { success } = useNotification();
  const [data, setData] = useState<AlarmStatisticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<number>(30);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [eventDetails, setEventDetails] = useState<EventAlarmDetailsResponse | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [openCreateAlarmDialog, setOpenCreateAlarmDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAlarmStatistics(period);
      setData(response);
    } catch (err: any) {
      console.error('Failed to fetch alarm statistics:', err);
      setError(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (_: React.MouseEvent<HTMLElement>, newPeriod: number | null) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod);
    }
  };

  const handleViewEventDetails = async (eventId: number) => {
    setSelectedEventId(eventId);
    setLoadingDetails(true);
    try {
      const response = await getEventAlarmDetails(eventId);
      setEventDetails(response);
    } catch (err) {
      console.error('Failed to fetch event alarm details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDialog = () => {
    setSelectedEventId(null);
    setEventDetails(null);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'push': return <PhoneAndroidIcon sx={{ fontSize: 18 }} />;
      case 'sms': return <SmsIcon sx={{ fontSize: 18 }} />;
      case 'email': return <EmailIcon sx={{ fontSize: 18 }} />;
      case 'in_app': return <NotificationsIcon sx={{ fontSize: 18 }} />;
      default: return <NotificationsIcon sx={{ fontSize: 18 }} />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'push': return '#2563eb';
      case 'sms': return '#16a34a';
      case 'email': return '#7c3aed';
      case 'in_app': return '#d97706';
      default: return '#64748b';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  if (!data) return null;

  // Format chart data
  const chartData = data.trend_chart?.map(item => ({
    date: new Date(item.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
    count: item.count,
  })) || [];

  // Calculate success rate as number
  const successRate = parseFloat(data.totals.success_rate?.replace('%', '') || '0');

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1e293b">
            {t('alarms.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Statistiques et suivi des notifications
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="contained"
            startIcon={<AddAlarmIcon />}
            onClick={() => setOpenCreateAlarmDialog(true)}
            sx={{
              bgcolor: '#2563eb',
              '&:hover': { bgcolor: '#1d4ed8' },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('alarms.createAlarm') || 'Créer une alarme'}
          </Button>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                border: '1px solid #e2e8f0',
                '&.Mui-selected': { bgcolor: '#2563eb', color: 'white', '&:hover': { bgcolor: '#1d4ed8' } },
              },
            }}
          >
            <ToggleButton value={7}>7j</ToggleButton>
            <ToggleButton value={30}>30j</ToggleButton>
            <ToggleButton value={90}>90j</ToggleButton>
            <ToggleButton value={365}>1an</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    {t('alarms.totalAlarms')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="#1e293b">
                    {data.totals.total_alarms?.toLocaleString() || 0}
                  </Typography>
                </Box>
                <Box sx={{ p: 1, bgcolor: '#dbeafe', borderRadius: 1 }}>
                  <NotificationsActiveIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    {t('alarms.inPeriod')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="#1e293b">
                    {data.totals.in_period?.toLocaleString() || 0}
                  </Typography>
                </Box>
                <Box sx={{ p: 1, bgcolor: '#f3e8ff', borderRadius: 1 }}>
                  <TrendingUpIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    {t('alarms.sent')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="#16a34a">
                    {data.totals.sent?.toLocaleString() || 0}
                  </Typography>
                </Box>
                <Box sx={{ p: 1, bgcolor: '#dcfce7', borderRadius: 1 }}>
                  <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    {t('alarms.pending')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="#d97706">
                    {data.totals.pending?.toLocaleString() || 0}
                  </Typography>
                </Box>
                <Box sx={{ p: 1, bgcolor: '#fef3c7', borderRadius: 1 }}>
                  <ScheduleIcon sx={{ color: '#d97706', fontSize: 20 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    {t('alarms.missed')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="#dc2626">
                    {data.totals.missed?.toLocaleString() || 0}
                  </Typography>
                </Box>
                <Box sx={{ p: 1, bgcolor: '#fef2f2', borderRadius: 1 }}>
                  <ErrorOutlineIcon sx={{ color: '#dc2626', fontSize: 20 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%', bgcolor: '#f0fdf4' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                {t('alarms.successRate')}
              </Typography>
              <Typography variant="h5" fontWeight={700} color="#16a34a">
                {data.totals.success_rate || '0%'}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={successRate}
                sx={{ mt: 1, height: 4, borderRadius: 0.5, bgcolor: '#dcfce7', '& .MuiLinearProgress-bar': { bgcolor: '#16a34a' } }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Trend Chart */}
      {chartData.length > 0 && (
        <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', mb: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
              {t('alarms.trendChart')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Évolution des alarmes sur la période sélectionnée
            </Typography>
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorAlarms" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    labelStyle={{ fontWeight: 600, color: '#1e293b' }}
                  />
                  <Area type="monotone" dataKey="count" name="Alarmes" stroke="#2563eb" strokeWidth={2} fillOpacity={1} fill="url(#colorAlarms)" />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* By Type */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('alarms.byType')}
              </Typography>
              <Stack spacing={2} sx={{ mt: 2 }}>
                {data.by_type && Object.entries(data.by_type).map(([type, count]) => {
                  const total = Object.values(data.by_type || {}).reduce((a, b) => a + (b || 0), 0);
                  const percentage = total > 0 ? ((count || 0) / total) * 100 : 0;
                  const inPeriod = data.sent_by_type_period?.[type as keyof typeof data.sent_by_type_period] || 0;

                  return (
                    <Box key={type}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Box sx={{ p: 0.75, bgcolor: `${getTypeColor(type)}15`, borderRadius: 1 }}>
                            {getTypeIcon(type)}
                          </Box>
                          <Box>
                            <Typography variant="body2" fontWeight={500} color="#1e293b" sx={{ textTransform: 'uppercase' }}>
                              {type}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              +{inPeriod} cette période
                            </Typography>
                          </Box>
                        </Stack>
                        <Typography variant="h6" fontWeight={700} color="#1e293b">
                          {(count || 0).toLocaleString()}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                          height: 6,
                          borderRadius: 0.5,
                          bgcolor: '#f1f5f9',
                          '& .MuiLinearProgress-bar': { bgcolor: getTypeColor(type), borderRadius: 0.5 }
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Events Stats */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <Box sx={{ p: 1, bgcolor: '#dbeafe', borderRadius: 1 }}>
                  <EventIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  {t('alarms.eventsStats')}
                </Typography>
              </Stack>

              {data.events && (
                <>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={4}>
                      <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight={700} color="#1e293b">
                          {data.events.total_events?.toLocaleString() || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{t('alarms.totalEvents')}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight={700} color="#2563eb">
                          {data.events.events_with_alarms?.toLocaleString() || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{t('alarms.withAlarms')}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ p: 2, bgcolor: '#dbeafe', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight={700} color="#2563eb">
                          {data.events.coverage || '0%'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{t('alarms.coverage')}</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {data.events.top_events && data.events.top_events.length > 0 && (
                    <>
                      <Typography variant="subtitle2" fontWeight={600} color="#1e293b" sx={{ mb: 2 }}>
                        {t('alarms.topEvents')}
                      </Typography>
                      <TableContainer sx={{ maxHeight: 250 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('alarms.eventTitle')}</TableCell>
                              <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('alarms.date')}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('alarms.alarmCount')}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0', width: 50 }}></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {data.events.top_events.map((event) => (
                              <TableRow key={event.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                <TableCell sx={{ py: 1.5 }}>
                                  <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 180 }}>
                                    {event.title}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(event.date).toLocaleDateString('fr-FR')}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 1.5 }}>
                                  <Typography variant="body2" fontWeight={600} color="#2563eb">
                                    {event.alarm_count}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center" sx={{ py: 1.5 }}>
                                  <IconButton size="small" onClick={() => handleViewEventDetails(event.id)} sx={{ color: '#64748b' }}>
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Users Stats */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <Box sx={{ p: 1, bgcolor: '#f3e8ff', borderRadius: 1 }}>
                  <PeopleIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  {t('alarms.usersStats')}
                </Typography>
              </Stack>

              {data.users && (
                <>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight={700} color="#1e293b">
                          {data.users.users_with_alarms?.toLocaleString() || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{t('alarms.usersWithAlarms')}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ p: 2, bgcolor: '#f3e8ff', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight={700} color="#7c3aed">
                          {Number(data.users.avg_alarms_per_user || 0).toFixed(1)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{t('alarms.avgAlarmsPerUser')}</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {data.users.top_users && data.users.top_users.length > 0 && (
                    <>
                      <Typography variant="subtitle2" fontWeight={600} color="#1e293b" sx={{ mb: 2 }}>
                        {t('alarms.topUsers')}
                      </Typography>
                      <TableContainer sx={{ maxHeight: 250 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>#</TableCell>
                              <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('userAnalytics.name')}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('alarms.alarmCount')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {data.users.top_users.map((user, index) => (
                              <TableRow key={user.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                <TableCell sx={{ py: 1.5 }}>
                                  {index < 3 ? (
                                    <Box
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: index === 0 ? '#fef3c7' : index === 1 ? '#f1f5f9' : '#fed7aa',
                                        color: index === 0 ? '#d97706' : index === 1 ? '#64748b' : '#c2410c',
                                        fontWeight: 700,
                                        fontSize: 12,
                                        borderRadius: 0.5,
                                      }}
                                    >
                                      {index + 1}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">{index + 1}</Typography>
                                  )}
                                </TableCell>
                                <TableCell sx={{ py: 1.5 }}>
                                  <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#7c3aed', fontSize: 12 }}>
                                      {user.name.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Typography variant="body2" fontWeight={500}>{user.name}</Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 1.5 }}>
                                  <Typography variant="body2" fontWeight={600} color="#7c3aed">{user.alarm_count}</Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Event Alarm Details Dialog */}
      <Dialog open={selectedEventId !== null} onClose={handleCloseDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>{t('alarms.eventDetails')}</Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : eventDetails ? (
            <Box>
              {/* Event Info */}
              <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1, mb: 3 }}>
                <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                  {eventDetails.event.title}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip
                    icon={<EventIcon />}
                    label={new Date(eventDetails.event.date).toLocaleDateString('fr-FR')}
                    size="small"
                    sx={{ bgcolor: '#dbeafe', color: '#2563eb' }}
                  />
                </Stack>
              </Box>

              {/* Summary */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={2.4}>
                  <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight={700} color="#1e293b">{eventDetails.summary.total_alarms}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('alarms.total')}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Box sx={{ p: 2, bgcolor: '#dcfce7', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight={700} color="#16a34a">{eventDetails.summary.sent}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('alarms.sent')}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Box sx={{ p: 2, bgcolor: '#fef3c7', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight={700} color="#d97706">{eventDetails.summary.pending}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('alarms.pending')}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Box sx={{ p: 2, bgcolor: '#fef2f2', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight={700} color="#dc2626">{eventDetails.summary.missed}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('alarms.missed')}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Box sx={{ p: 2, bgcolor: '#f3e8ff', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight={700} color="#7c3aed">{eventDetails.summary.unique_users}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('alarms.uniqueUsers')}</Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* By Type */}
              <Typography variant="subtitle2" fontWeight={600} color="#1e293b" sx={{ mb: 2 }}>{t('alarms.byType')}</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
                {eventDetails.by_type && Object.entries(eventDetails.by_type).map(([type, count]) => (
                  <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: `${getTypeColor(type)}15`, borderRadius: 1 }}>
                    {getTypeIcon(type)}
                    <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                      {type}: {count}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              {/* Timeline */}
              {eventDetails.timeline && eventDetails.timeline.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} color="#1e293b" sx={{ mb: 2 }}>{t('alarms.timeline')}</Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('alarms.type')}</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('alarms.scheduled')}</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('alarms.status')}</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('alarms.sentAt')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {eventDetails.timeline.map((item) => (
                          <TableRow key={item.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                            <TableCell sx={{ py: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getTypeIcon(item.type)}
                                <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'uppercase' }}>
                                  {item.type}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography variant="body2">{new Date(item.scheduled_at).toLocaleString('fr-FR')}</Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              {item.sent ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#16a34a' }}>
                                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                                  <Typography variant="body2" fontWeight={500}>{t('alarms.sent')}</Typography>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#d97706' }}>
                                  <ScheduleIcon sx={{ fontSize: 16 }} />
                                  <Typography variant="body2" fontWeight={500}>{t('alarms.pending')}</Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                {item.sent_at ? new Date(item.sent_at).toLocaleString('fr-FR') : '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Create Alarm Dialog (sans événement requis) */}
      <CreateAlarmDialog
        open={openCreateAlarmDialog}
        onClose={() => setOpenCreateAlarmDialog(false)}
        onSuccess={() => {
          setOpenCreateAlarmDialog(false);
          fetchData(); // Rafraîchir les statistiques
          success(t('admin.notifications.createSuccess') || 'Alarme créée avec succès');
        }}
      />
    </Box>
  );
};

export default AlarmsTab;
